import PropTypes from 'prop-types';
import { useEffect } from 'react';
import Spinner from '../Spinner';
import LaserOverlay from './LaserOverlay';
import { viewportStyle } from './sceneViewportUtils';
import useLaserOverlay from './useLaserOverlay';
import { useSceneAssetCollection } from './useSceneAssetCollection';
import useSceneRenderables from './useSceneRenderables';
import useThreeViewport from './useThreeViewport';
import useViewportCameraController from './useViewportCameraController';

function SceneViewport({
    appearance,
    cameraCommand,
    interactionMode,
    resourceScopeId,
    onCameraViewStateChange,
    onRuntimeModelStateChange,
    onUserCameraInteraction,
    sceneModels,
    visibleModelIds,
}) {
    const { assetsById } = useSceneAssetCollection({
        activeModelIds: visibleModelIds,
        enabled: true,
        resourceScopeId,
        sceneModels,
    });
    const {
        cameraRef,
        controlsRef,
        mountRef,
        rendererDomElement,
        rendererRef,
        sceneRef,
        stageRef,
    } = useThreeViewport({
        onUserCameraInteraction,
    });
    const isViewportReady = Boolean(
        rendererDomElement
        && rendererRef.current
        && sceneRef.current,
    );
    const {
        buildableSceneModels,
        isSceneLoading,
        renderHandlesRef,
        renderStatuses,
        runtimeModelStateById,
        visibleBoundsVersion,
    } = useSceneRenderables({
        appearance,
        assetsById,
        isViewportReady,
        sceneModels,
        stageRef,
        visibleModelIds,
    });
    useViewportCameraController({
        buildableSceneModels,
        cameraCommand,
        cameraRef,
        controlsRef,
        isViewportReady,
        onCameraViewStateChange,
        renderHandlesRef,
        renderStatuses,
        sceneModels,
        visibleBoundsVersion,
    });
    const {
        cursorStyle,
        laserCanvasRef,
        laserCursorRef,
        laserStyle,
    } = useLaserOverlay({
        controlsRef,
        interactionMode,
        rendererDomElement,
    });

    useEffect(() => {
        onRuntimeModelStateChange?.(runtimeModelStateById);
    }, [onRuntimeModelStateChange, runtimeModelStateById]);

    return (
        <div style={viewportStyle}>
            <div
                ref={mountRef}
                style={{
                    height: '100%',
                    overflow: 'hidden',
                    width: '100%',
                }}
            />
            <LaserOverlay
                cursorRef={laserCursorRef}
                cursorStyle={cursorStyle}
                laserRef={laserCanvasRef}
                laserStyle={laserStyle}
            />
            {isSceneLoading && (
                <Spinner type="center" />
            )}
        </div>
    );
}

SceneViewport.propTypes = {
    appearance: PropTypes.shape({
        defaultAppearance: PropTypes.shape({
            showTexture: PropTypes.bool,
        }),
        modelAppearances: PropTypes.objectOf(
            PropTypes.shape({
                showTexture: PropTypes.bool,
            }),
        ),
    }),
    cameraCommand: PropTypes.shape({
        nonce: PropTypes.number,
        type: PropTypes.oneOf([
            'top',
            'right',
            'left',
            'bottom',
            'front',
            'behind',
            'resetView',
        ]),
    }),
    interactionMode: PropTypes.oneOf([
        'camera',
        'laser',
    ]),
    resourceScopeId: PropTypes.string,
    onCameraViewStateChange: PropTypes.func,
    onRuntimeModelStateChange: PropTypes.func,
    onUserCameraInteraction: PropTypes.func,
    sceneModels: PropTypes.arrayOf(
        PropTypes.shape({
            descriptorKey: PropTypes.string.isRequired,
            format: PropTypes.oneOf([
                'stl',
                'ply',
            ]).isRequired,
            geometryUrl: PropTypes.string.isRequired,
            id: PropTypes.string.isRequired,
            preservePlacement: PropTypes.bool,
            scanId: PropTypes.string,
            shouldBuild: PropTypes.bool,
            textureUrl: PropTypes.string,
        }),
    ),
    visibleModelIds: PropTypes.arrayOf(PropTypes.string),
};

SceneViewport.defaultProps = {
    appearance: {
        defaultAppearance: {
            showTexture: true,
        },
        modelAppearances: {},
    },
    cameraCommand: null,
    interactionMode: 'camera',
    resourceScopeId: null,
    onCameraViewStateChange: null,
    onRuntimeModelStateChange: null,
    onUserCameraInteraction: null,
    sceneModels: [],
    visibleModelIds: [],
};

export default SceneViewport;
